/**
 * Shared base implementation for QuickBooks customer reference / name
 * management. Both contact-scoped (`quickbooks_contact_ref` /
 * `quickbooks_contact_name`) and company-scoped (`quickbooks_customer_ref` /
 * `quickbooks_customer_name`) variants delegate to these helpers.
 *
 * Behavior is intentionally identical to the previous duplicated
 * implementations — the only difference between variants is the field name
 * accessed on the parent entity.
 */

import type { QuickBooksInvoice } from "@/lib/types/quickbooks/entities/invoice";
import type { MultiCompanyItem } from "@/lib/types/quickbooks/multi-company";

/** An invoice as displayed in the multi-company invoice UI: the QBO invoice plus its source company metadata. */
export type InvoiceDisplayItem = MultiCompanyItem<QuickBooksInvoice>;

export interface QuickBooksCustomerRef {
  customerId: string;
  realmId?: string;
}

/**
 * Splits the packed name column into entries: a comma that STARTS a new
 * `customerId:` entry, never a comma inside a customer name.
 *
 * Deliberately `[^,:;]+` rather than `\d+` — customer ids are opaque text here,
 * even though real QuickBooks ids are numeric. Kept in agreement with
 * `public.cqc_parse_customer_names` in the link-table migration.
 */
export const CUSTOMER_NAME_ENTRY_SEPARATOR = /,(?=[^,:;]+:)/;

export interface QuickBooksCustomerName {
  customerId: string;
  customerName: string;
  realmId?: string;
}

// ============================================================================
// QUICKBOOKS CUSTOMER REF MANAGEMENT
// ============================================================================

/**
 * Parse a QuickBooks customer ref field which stores multiple customer
 * references in the format: "customer1;realmId,customer2;realmId"
 */
export function parseCustomerRef(
  refString: string | null | undefined
): QuickBooksCustomerRef[] {
  if (!refString) return [];

  return refString
    .split(",")
    .map((ref) => ref.trim())
    .filter(Boolean)
    .map((ref) => {
      const [customerId, realmId] = ref.split(";");
      return {
        customerId: customerId?.trim() || "",
        realmId: realmId?.trim() || undefined,
      };
    })
    .filter((ref) => ref.customerId);
}

/**
 * Serialize QuickBooks customer references back to the storage format.
 */
export function serializeCustomerRef(refs: QuickBooksCustomerRef[]): string {
  return refs
    .filter((ref) => ref.customerId)
    .map((ref) => (ref.realmId ? `${ref.customerId};${ref.realmId}` : ref.customerId))
    .join(",");
}

/**
 * Add a new QuickBooks customer reference to an existing ref string.
 * If the customer ID already exists the entry is updated in place.
 */
export function addCustomerRef(
  existingRef: string | null | undefined,
  customerId: string,
  realmId?: string
): string {
  const existingRefs = parseCustomerRef(existingRef);

  const existingIndex = existingRefs.findIndex((ref) => ref.customerId === customerId);

  if (existingIndex !== -1) {
    existingRefs[existingIndex] = { customerId, realmId };
  } else {
    existingRefs.push({ customerId, realmId });
  }

  return serializeCustomerRef(existingRefs);
}

/**
 * Remove a QuickBooks customer reference from an existing ref string.
 */
export function removeCustomerRef(
  existingRef: string | null | undefined,
  customerId: string
): string {
  const existingRefs = parseCustomerRef(existingRef);
  const filteredRefs = existingRefs.filter((ref) => ref.customerId !== customerId);
  return serializeCustomerRef(filteredRefs);
}

// ============================================================================
// QUICKBOOKS CUSTOMER NAME MANAGEMENT
// ============================================================================

/**
 * Parse a QuickBooks customer name field which stores multiple customer names
 * in the format: "customerId:customerName;realmId,customerId2:customerName2;realmId"
 *
 * The encoding is ambiguous the moment a customer name contains a comma — and
 * real ones do: "Bocci Engineering, LLC", "Pradel Enterprises, Inc". A plain
 * `split(",")` cut those names at the comma and then DROPPED the orphan fragment
 * (it has no colon), so Hub displayed "Bocci Engineering" while the stored string
 * still said ", LLC". Splitting only on a comma that STARTS a new entry (one
 * followed by `<digits>:`) reads the name whole.
 *
 * `public.cqc_parse_customer_names` in the link-table migration is the SQL twin of
 * this function and must stay in agreement — it feeds the same names into
 * `company_quickbooks_customers`. Both use the same entry separator and the same
 * first-colon / last-semicolon rule.
 */
export function parseCustomerName(
  nameString: string | null | undefined
): QuickBooksCustomerName[] {
  if (!nameString) return [];

  return nameString
    .split(CUSTOMER_NAME_ENTRY_SEPARATOR)
    .map((ref) => ref.trim())
    .filter(Boolean)
    .map((ref) => {
      // Only the FIRST colon separates the id from the rest: a name may itself
      // contain a colon ("Acme: The Sequel"), and `split(":")` destructuring
      // silently truncated it at the second one.
      const colonIndex = ref.indexOf(":");
      if (colonIndex <= 0) return null;
      const customerIdPart = ref.slice(0, colonIndex);
      const nameAndRealm = ref.slice(colonIndex + 1);
      if (!customerIdPart || !nameAndRealm) return null;

      // Check if nameAndRealm contains realm (has semicolon)
      const lastSemicolonIndex = nameAndRealm.lastIndexOf(";");
      if (lastSemicolonIndex !== -1) {
        // Has realm
        const customerName = nameAndRealm.substring(0, lastSemicolonIndex);
        const realmId = nameAndRealm.substring(lastSemicolonIndex + 1);
        return {
          customerId: customerIdPart.trim(),
          customerName: customerName.trim(),
          realmId: realmId.trim() || undefined,
        };
      } else {
        // No realm
        return {
          customerId: customerIdPart.trim(),
          customerName: nameAndRealm.trim(),
        };
      }
    })
    .filter(
      (ref): ref is QuickBooksCustomerName =>
        ref !== null && !!ref.customerId && !!ref.customerName
    );
}

/**
 * Serialize QuickBooks customer names back to the storage format.
 */
export function serializeCustomerName(names: QuickBooksCustomerName[]): string {
  return names
    .filter((name) => name.customerId && name.customerName)
    .map((name) => {
      const base = `${name.customerId}:${name.customerName}`;
      return name.realmId ? `${base};${name.realmId}` : base;
    })
    .join(",");
}

/**
 * Add a new QuickBooks customer name to an existing name string.
 * If the customer ID already exists the entry is updated in place.
 */
export function addCustomerName(
  existingName: string | null | undefined,
  customerId: string,
  customerName: string,
  realmId?: string
): string {
  const existingNames = parseCustomerName(existingName);

  const existingIndex = existingNames.findIndex((name) => name.customerId === customerId);

  if (existingIndex !== -1) {
    existingNames[existingIndex] = { customerId, customerName, realmId };
  } else {
    existingNames.push({ customerId, customerName, realmId });
  }

  return serializeCustomerName(existingNames);
}

/**
 * Remove a QuickBooks customer name from an existing name string.
 */
export function removeCustomerName(
  existingName: string | null | undefined,
  customerId: string
): string {
  const existingNames = parseCustomerName(existingName);
  const filteredNames = existingNames.filter((name) => name.customerId !== customerId);
  return serializeCustomerName(filteredNames);
}

/**
 * Look up a customer name within a parsed list by customer ID.
 */
export function findCustomerName(
  names: QuickBooksCustomerName[],
  customerId: string
): string | undefined {
  const nameRef = names.find((name) => name.customerId === customerId);
  return nameRef?.customerName;
}

/**
 * Generic implementation of getInvoiceCustomerDisplayName that operates on the
 * raw name string (`quickbooks_contact_name` or `quickbooks_customer_name`).
 *
 * Behavior matches the previous per-entity implementations bit-for-bit:
 *   - returns "Unknown Customer" when invoiceItem/data missing
 *   - falls back to `invoiceItem.data.CustomerRef?.name` when no nameString or
 *     no customerId
 *   - matches realmId only when both entry and invoice provide one
 *
 * `invoiceItem` is the multi-company invoice display shape (`{ data, company }`).
 */
export function getInvoiceCustomerDisplayNameGeneric(
  invoiceItem: InvoiceDisplayItem | null | undefined,
  nameString: string | null | undefined
): string {
  // Add null checks for invoiceItem structure
  if (!invoiceItem || !invoiceItem.data) {
    return "Unknown Customer";
  }

  if (!nameString) {
    return invoiceItem.data.CustomerRef?.name || "Unknown Customer";
  }

  const customerId = invoiceItem.data.CustomerRef?.value;
  const realmId = invoiceItem.company?.realmId;

  if (!customerId) {
    return invoiceItem.data.CustomerRef?.name || "Unknown Customer";
  }

  // Parse the name field to find matching customer name
  // Format: "customerId:customerName;realmId,customerId2:customerName2;realmId"
  const nameEntries = nameString.split(",");

  for (const entry of nameEntries) {
    const trimmed = entry.trim();
    if (!trimmed) continue;

    // Split by colon to separate customerId and name/realm
    const colonIndex = trimmed.indexOf(":");
    if (colonIndex === -1) continue;

    const entryCustomerId = trimmed.substring(0, colonIndex).trim();
    const nameAndRealm = trimmed.substring(colonIndex + 1);

    if (entryCustomerId === customerId) {
      // Check if realm matches (if realm is specified)
      const lastSemicolonIndex = nameAndRealm.lastIndexOf(";");
      if (lastSemicolonIndex !== -1) {
        const customerName = nameAndRealm.substring(0, lastSemicolonIndex).trim();
        const entryRealmId = nameAndRealm.substring(lastSemicolonIndex + 1).trim();

        // If realm is specified in the entry, it must match
        if (realmId && entryRealmId && entryRealmId !== realmId) {
          continue;
        }

        return customerName;
      } else {
        // No realm specified, just return the name
        return nameAndRealm.trim();
      }
    }
  }

  // Fallback to QuickBooks data
  return invoiceItem.data.CustomerRef?.name || "Unknown Customer";
}
